import { Fragment, useEffect, useState } from 'react';
import axios from 'axios';
import { useHistory } from 'react-router-dom';
import Container from '../../components/Container';
import Header from '../../components/Header';
import Button from '../../components/Button';
import FlexContainer from '../../components/FlexContainer';

interface Employee {
    _id: string;
    name: string;
    email: string;
    hourlyRate: number;
    role?: string;
    active: boolean;
}

interface PayrollConfig {
    _id?: string;
    cadenceWeeks: number;
    anchorDate: string;
}

interface PeriodRow {
    employeeId: string;
    name: string;
    email: string;
    hourlyRate: number;
    hours: number;
    gross: number;
    openPunch: boolean;
}

interface PeriodSummary {
    periodStartUtc: string;
    periodEndUtc: string;
    cadenceWeeks: number;
    rows: PeriodRow[];
}

interface Punch {
    _id: string;
    employeeId: string;
    clockInUtc: string;
    clockOutUtc: string | null;
    autoClosed?: boolean;
}

interface QboStatus {
    configured: boolean;
    connected: boolean;
    realmId?: string;
    environment: string;
    redirectUri: string;
    accessTokenExpiresAt?: string;
    refreshTokenExpiresAt?: string;
    lastRun?: {
        periodStart: string;
        periodEnd: string;
        runAtUtc: string;
        activitiesCreated: number;
        activitiesUpdated: number;
        unmatchedEmployees: string[];
        errors: string[];
    };
}

interface QboRunResult {
    periodStart: string;
    periodEnd: string;
    activitiesCreated: number;
    unmatchedEmployees: string[];
    errors: string[];
    details: Array<{
        employee: string;
        date: string;
        hours: number;
        status: 'created' | 'skipped' | 'error';
        message?: string;
    }>;
}

interface StatsByEmployee {
    employeeId: string;
    name: string;
    email: string;
    hourlyRate: number;
    hours: number;
    days: number;
    gross: number;
}

interface StatsByDayEmp {
    employeeId: string;
    name: string;
    hours: number;
}

interface StatsByDay {
    date: string;
    hours: number;
    employees: StatsByDayEmp[];
}

interface StatsResponse {
    from: string;
    to: string;
    totals: {
        hours: number;
        gross: number;
        days: number;
        employees: number;
    };
    byEmployee: StatsByEmployee[];
    byDay: StatsByDay[];
}

type StatsPreset = 'today' | 'week' | 'month' | 'ytd' | 'custom';

type Tab = 'employees' | 'config' | 'period' | 'stats' | 'quickbooks';

const blankForm = { name: '', email: '', hourlyRate: 0, role: '', active: true };

const PayrollAdminPage = () => {
    const history = useHistory();
    const [tab, setTab] = useState<Tab>('period');

    const [qboStatus, setQboStatus] = useState<QboStatus | null>(null);
    const [qboBusy, setQboBusy] = useState(false);
    const [qboRun, setQboRun] = useState<QboRunResult | null>(null);
    const [qboBanner, setQboBanner] = useState<string | null>(null);

    const todayIso = () => new Date().toISOString().slice(0, 10);
    const [statsPreset, setStatsPreset] = useState<StatsPreset>('week');
    const [statsFrom, setStatsFrom] = useState<string>(todayIso());
    const [statsTo, setStatsTo] = useState<string>(todayIso());
    const [stats, setStats] = useState<StatsResponse | null>(null);
    const [statsLoading, setStatsLoading] = useState(false);
    const [statsExpandedDate, setStatsExpandedDate] = useState<string | null>(
        null
    );

    const [employees, setEmployees] = useState<Employee[] | null>(null);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [form, setForm] = useState({ ...blankForm });

    const [config, setConfig] = useState<PayrollConfig | null>(null);
    const [configForm, setConfigForm] = useState({ cadenceWeeks: 2, anchorDate: '' });

    const [period, setPeriod] = useState<PeriodSummary | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    const [expandedEmployeeId, setExpandedEmployeeId] = useState<string | null>(
        null
    );
    const [punches, setPunches] = useState<Record<string, Punch[]>>({});
    const [punchEdits, setPunchEdits] = useState<
        Record<string, { in: string; out: string }>
    >({});
    const [newPunch, setNewPunch] = useState<{ in: string; out: string }>({
        in: '',
        out: '',
    });
    const [punchBusy, setPunchBusy] = useState(false);
    const [punchError, setPunchError] = useState<string | null>(null);

    const loadEmployees = async () => {
        const { data } = await axios.get<Employee[]>('/api/timeclock/employees');
        setEmployees(data);
    };
    const loadConfig = async () => {
        const { data } = await axios.get<PayrollConfig>('/api/timeclock/config');
        setConfig(data);
        setConfigForm({
            cadenceWeeks: data.cadenceWeeks,
            anchorDate: data.anchorDate?.slice(0, 10) || '',
        });
    };
    const loadPeriod = async () => {
        const { data } = await axios.get<PeriodSummary>('/api/timeclock/period');
        setPeriod(data);
    };

    const utcToLocalInput = (utc: string | null | undefined): string => {
        if (!utc) return '';
        const d = new Date(utc);
        if (Number.isNaN(d.getTime())) return '';
        const pad = (n: number) => String(n).padStart(2, '0');
        return (
            d.getFullYear() +
            '-' +
            pad(d.getMonth() + 1) +
            '-' +
            pad(d.getDate()) +
            'T' +
            pad(d.getHours()) +
            ':' +
            pad(d.getMinutes())
        );
    };
    const localInputToIso = (s: string): string | null => {
        if (!s) return null;
        const d = new Date(s);
        return Number.isNaN(d.getTime()) ? null : d.toISOString();
    };

    const seedPunchEdits = (list: Punch[]) => {
        const edits: Record<string, { in: string; out: string }> = {};
        list.forEach((p) => {
            edits[p._id] = {
                in: utcToLocalInput(p.clockInUtc),
                out: utcToLocalInput(p.clockOutUtc),
            };
        });
        setPunchEdits((prev) => ({ ...prev, ...edits }));
    };

    const reloadPunches = async (empId: string) => {
        if (!period) return;
        const { data } = await axios.get<Punch[]>('/api/timeclock/punches', {
            params: {
                employeeId: empId,
                from: period.periodStartUtc,
                to: period.periodEndUtc,
            },
        });
        setPunches((prev) => ({ ...prev, [empId]: data }));
        seedPunchEdits(data);
    };

    const toggleExpand = async (empId: string) => {
        setPunchError(null);
        setNewPunch({ in: '', out: '' });
        if (expandedEmployeeId === empId) {
            setExpandedEmployeeId(null);
            return;
        }
        setExpandedEmployeeId(empId);
        try {
            await reloadPunches(empId);
        } catch (e: any) {
            setPunchError(
                e?.response?.data?.message || 'Failed to load punches.'
            );
        }
    };

    const savePunch = async (punch: Punch) => {
        const edit = punchEdits[punch._id];
        if (!edit) return;
        if (!edit.in) {
            setPunchError('Clock-in required.');
            return;
        }
        setPunchBusy(true);
        setPunchError(null);
        try {
            await axios.patch(`/api/timeclock/punches/${punch._id}`, {
                clockInUtc: localInputToIso(edit.in),
                clockOutUtc: edit.out ? localInputToIso(edit.out) : null,
            });
            await reloadPunches(punch.employeeId);
            await loadPeriod();
        } catch (e: any) {
            setPunchError(e?.response?.data?.message || 'Save failed.');
        } finally {
            setPunchBusy(false);
        }
    };

    const removePunch = async (punch: Punch) => {
        if (!window.confirm('Delete this punch?')) return;
        setPunchBusy(true);
        setPunchError(null);
        try {
            await axios.delete(`/api/timeclock/punches/${punch._id}`);
            await reloadPunches(punch.employeeId);
            await loadPeriod();
        } catch (e: any) {
            setPunchError(e?.response?.data?.message || 'Delete failed.');
        } finally {
            setPunchBusy(false);
        }
    };

    const addPunch = async (empId: string) => {
        if (!newPunch.in) {
            setPunchError('Clock-in required.');
            return;
        }
        setPunchBusy(true);
        setPunchError(null);
        try {
            await axios.post('/api/timeclock/punches', {
                employeeId: empId,
                clockInUtc: localInputToIso(newPunch.in),
                clockOutUtc: newPunch.out ? localInputToIso(newPunch.out) : null,
            });
            setNewPunch({ in: '', out: '' });
            await reloadPunches(empId);
            await loadPeriod();
        } catch (e: any) {
            setPunchError(e?.response?.data?.message || 'Add failed.');
        } finally {
            setPunchBusy(false);
        }
    };

    const punchHours = (p: Punch): number => {
        const start = new Date(p.clockInUtc).getTime();
        const end = p.clockOutUtc ? new Date(p.clockOutUtc).getTime() : Date.now();
        return Math.max(0, (end - start) / 3_600_000);
    };

    useEffect(() => {
        (async () => {
            try {
                await Promise.all([loadEmployees(), loadConfig(), loadPeriod()]);
            } catch (e: any) {
                setError(e?.response?.data?.message || 'Failed to load.');
            }
        })();
    }, []);

    const startEdit = (e: Employee) => {
        setEditingId(e._id);
        setForm({
            name: e.name,
            email: e.email,
            hourlyRate: e.hourlyRate,
            role: e.role || '',
            active: e.active,
        });
    };
    const cancelEdit = () => {
        setEditingId(null);
        setForm({ ...blankForm });
    };
    const saveEmployee = async () => {
        setBusy(true);
        setError(null);
        try {
            if (editingId) {
                await axios.patch(`/api/timeclock/employees/${editingId}`, form);
            } else {
                await axios.post('/api/timeclock/employees', form);
            }
            cancelEdit();
            await loadEmployees();
            await loadPeriod();
        } catch (e: any) {
            setError(e?.response?.data?.message || 'Save failed.');
        } finally {
            setBusy(false);
        }
    };
    const removeEmployee = async (id: string) => {
        if (!window.confirm('Delete this employee? Punch history is preserved.')) return;
        setBusy(true);
        try {
            await axios.delete(`/api/timeclock/employees/${id}`);
            await loadEmployees();
            await loadPeriod();
        } catch (e: any) {
            setError(e?.response?.data?.message || 'Delete failed.');
        } finally {
            setBusy(false);
        }
    };
    const saveConfig = async () => {
        setBusy(true);
        setError(null);
        try {
            await axios.put('/api/timeclock/config', configForm);
            await loadConfig();
            await loadPeriod();
        } catch (e: any) {
            setError(e?.response?.data?.message || 'Save failed.');
        } finally {
            setBusy(false);
        }
    };

    const loadQboStatus = async () => {
        try {
            const { data } = await axios.get<QboStatus>(
                '/api/quickbooks/status'
            );
            setQboStatus(data);
        } catch {
            // silent — endpoint may not exist yet on older server builds
        }
    };

    useEffect(() => {
        loadQboStatus();
        const params = new URLSearchParams(window.location.search);
        const qbo = params.get('qbo');
        if (qbo === 'connected') {
            setQboBanner('Connected to QuickBooks.');
            setTab('quickbooks');
        } else if (qbo === 'error') {
            setQboBanner(
                'QuickBooks connect failed: ' + (params.get('msg') || 'unknown')
            );
            setTab('quickbooks');
        }
        if (qbo) {
            window.history.replaceState({}, '', window.location.pathname);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const qboConnect = () => {
        window.location.href = '/api/quickbooks/connect';
    };

    const qboDisconnect = async () => {
        if (!window.confirm('Disconnect from QuickBooks?')) return;
        setQboBusy(true);
        try {
            await axios.post('/api/quickbooks/disconnect');
            setQboBanner('Disconnected from QuickBooks.');
            setQboRun(null);
            await loadQboStatus();
        } finally {
            setQboBusy(false);
        }
    };

    const runPayroll = async () => {
        if (
            !window.confirm(
                'Upload the current pay period to QuickBooks? Time activities will be created for each employee+day worked.'
            )
        )
            return;
        setQboBusy(true);
        setQboRun(null);
        setQboBanner(null);
        try {
            const { data } = await axios.post<QboRunResult>(
                '/api/quickbooks/run-payroll'
            );
            setQboRun(data);
            setQboBanner(
                'Payroll run: ' + data.activitiesCreated + ' time activities created.'
            );
            await loadQboStatus();
        } catch (e: any) {
            setQboBanner(
                'Run payroll failed: ' + (e?.response?.data?.message || e?.message || 'error')
            );
        } finally {
            setQboBusy(false);
        }
    };

    const computePreset = (preset: StatsPreset) => {
        const now = new Date();
        const to = new Date(now);
        to.setUTCHours(0, 0, 0, 0);
        to.setUTCDate(to.getUTCDate() + 1);
        const toIso = to.toISOString().slice(0, 10);
        let from = new Date(to);
        if (preset === 'today') {
            from.setUTCDate(from.getUTCDate() - 1);
        } else if (preset === 'week') {
            // last 7 days rolling
            from.setUTCDate(from.getUTCDate() - 7);
        } else if (preset === 'month') {
            from.setUTCDate(from.getUTCDate() - 30);
        } else if (preset === 'ytd') {
            from = new Date(Date.UTC(now.getUTCFullYear(), 0, 1));
        }
        return {
            from: from.toISOString().slice(0, 10),
            to: toIso,
        };
    };

    const loadStats = async (
        fromDate: string,
        toDate: string
    ) => {
        setStatsLoading(true);
        try {
            const { data } = await axios.get<StatsResponse>(
                '/api/timeclock/stats',
                { params: { from: fromDate, to: toDate } }
            );
            setStats(data);
            setStatsExpandedDate(null);
        } catch (e: any) {
            setError(e?.response?.data?.message || 'Failed to load stats.');
        } finally {
            setStatsLoading(false);
        }
    };

    const applyPreset = (preset: StatsPreset) => {
        setStatsPreset(preset);
        if (preset === 'custom') return;
        const { from, to } = computePreset(preset);
        setStatsFrom(from);
        setStatsTo(to);
        loadStats(from, to);
    };

    useEffect(() => {
        if (tab === 'stats' && !stats) {
            applyPreset('week');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab]);

    const fmtHours = (h: number) => {
        const hh = Math.floor(h);
        const mm = Math.round((h - hh) * 60);
        if (hh === 0 && mm === 0) return '0h';
        if (hh === 0) return mm + 'm';
        if (mm === 0) return hh + 'h';
        return hh + 'h ' + mm + 'm';
    };

    const fmtDay = (iso: string) => {
        const [y, m, d] = iso.split('-').map(Number);
        const dt = new Date(Date.UTC(y, m - 1, d));
        return dt.toLocaleDateString(undefined, {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
        });
    };

    const tabBtn = (id: Tab, label: string) => (
        <Button
            key={id}
            color={tab === id ? 'green' : 'blue'}
            margin={true}
            onClick={() => setTab(id)}
        >
            {label}
        </Button>
    );

    const fmtDate = (iso: string) =>
        new Date(iso).toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
        });

    return (
        <Container containerName={'landing'}>
            <Header
                title={'Payroll Admin'}
                subtitle={''}
                bottomBorder={true}
                padding={true}
            />
            <FlexContainer>
                <div>
                    {tabBtn('period', 'Current Period')}
                    {tabBtn('stats', 'Statistics')}
                    {tabBtn('employees', 'Employees')}
                    {tabBtn('config', 'Payroll Config')}
                    {tabBtn('quickbooks', 'QuickBooks')}
                </div>

                {error && (
                    <h3 className="transaction-container__text">
                        <span className="red-span">{error}</span>
                    </h3>
                )}

                {tab === 'period' && period && (
                    <>
                        <h3 className="transaction-container__text">
                            Period:{' '}
                            <span className="red-span">
                                {fmtDate(period.periodStartUtc)} —{' '}
                                {fmtDate(period.periodEndUtc)}
                            </span>{' '}
                            ({period.cadenceWeeks}-week)
                        </h3>
                        {period.rows.length === 0 ? (
                            <h3 className="transaction-container__text">
                                No employees yet.
                            </h3>
                        ) : (
                            <>
                                <p
                                    style={{
                                        textAlign: 'center',
                                        fontSize: '1rem',
                                        marginTop: 0,
                                        marginBottom: '0.5rem',
                                        fontStyle: 'italic',
                                    }}
                                >
                                    Tap any employee to view & edit their punches for this period.
                                </p>
                                <table className="transaction-container__form--table">
                                    <thead>
                                        <tr>
                                            <th>Name</th>
                                            <th>Email</th>
                                            <th>Hours</th>
                                            <th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {period.rows.map((r) => {
                                            const isOpen =
                                                expandedEmployeeId ===
                                                r.employeeId;
                                            const empPunches =
                                                punches[r.employeeId] || [];
                                            return (
                                                <Fragment key={r.employeeId}>
                                                    <tr
                                                        onClick={() =>
                                                            toggleExpand(
                                                                r.employeeId
                                                            )
                                                        }
                                                        style={{
                                                            cursor: 'pointer',
                                                            background: isOpen
                                                                ? 'rgba(34,197,94,0.12)'
                                                                : undefined,
                                                        }}
                                                    >
                                                        <td className="transaction-container__form--table-stage">
                                                            {isOpen ? '▾ ' : '▸ '}
                                                            {r.name.toUpperCase()}
                                                        </td>
                                                        <td className="transaction-container__form--table-value">
                                                            {r.email}
                                                        </td>
                                                        <td className="transaction-container__form--table-value">
                                                            {r.hours.toFixed(2)}
                                                        </td>
                                                        <td className="transaction-container__form--table-value">
                                                            {r.openPunch
                                                                ? 'CLOCKED IN'
                                                                : '—'}
                                                        </td>
                                                    </tr>
                                                    {isOpen && (
                                                        <tr>
                                                            <td
                                                                colSpan={4}
                                                                style={{
                                                                    padding:
                                                                        '0.75rem 1rem',
                                                                    background:
                                                                        'rgba(34,197,94,0.06)',
                                                                }}
                                                            >
                                                                {punchError && (
                                                                    <p
                                                                        style={{
                                                                            color: '#ef4444',
                                                                            fontWeight: 600,
                                                                            margin: '0 0 0.5rem',
                                                                        }}
                                                                    >
                                                                        {punchError}
                                                                    </p>
                                                                )}
                                                                <div
                                                                    style={{
                                                                        overflowX:
                                                                            'auto',
                                                                    }}
                                                                >
                                                                    <table
                                                                        style={{
                                                                            width: '100%',
                                                                            borderCollapse:
                                                                                'collapse',
                                                                            fontSize:
                                                                                '1rem',
                                                                        }}
                                                                    >
                                                                        <thead>
                                                                            <tr
                                                                                style={{
                                                                                    borderBottom:
                                                                                        '2px solid #22c55e',
                                                                                }}
                                                                            >
                                                                                <th
                                                                                    style={{
                                                                                        textAlign:
                                                                                            'left',
                                                                                        padding:
                                                                                            '0.4rem 0.5rem',
                                                                                    }}
                                                                                >
                                                                                    Clock In
                                                                                </th>
                                                                                <th
                                                                                    style={{
                                                                                        textAlign:
                                                                                            'left',
                                                                                        padding:
                                                                                            '0.4rem 0.5rem',
                                                                                    }}
                                                                                >
                                                                                    Clock Out
                                                                                </th>
                                                                                <th
                                                                                    style={{
                                                                                        textAlign:
                                                                                            'right',
                                                                                        padding:
                                                                                            '0.4rem 0.5rem',
                                                                                    }}
                                                                                >
                                                                                    Hours
                                                                                </th>
                                                                                <th
                                                                                    style={{
                                                                                        padding:
                                                                                            '0.4rem 0.5rem',
                                                                                    }}
                                                                                />
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {empPunches.length ===
                                                                            0 ? (
                                                                                <tr>
                                                                                    <td
                                                                                        colSpan={
                                                                                            4
                                                                                        }
                                                                                        style={{
                                                                                            padding:
                                                                                                '0.5rem',
                                                                                            fontStyle:
                                                                                                'italic',
                                                                                        }}
                                                                                    >
                                                                                        No punches in this period.
                                                                                    </td>
                                                                                </tr>
                                                                            ) : (
                                                                                empPunches.map(
                                                                                    (
                                                                                        p
                                                                                    ) => {
                                                                                        const edit =
                                                                                            punchEdits[
                                                                                                p
                                                                                                    ._id
                                                                                            ] || {
                                                                                                in: '',
                                                                                                out: '',
                                                                                            };
                                                                                        return (
                                                                                            <tr
                                                                                                key={
                                                                                                    p._id
                                                                                                }
                                                                                                style={{
                                                                                                    borderBottom:
                                                                                                        '1px solid rgba(128,128,128,0.3)',
                                                                                                }}
                                                                                            >
                                                                                                <td
                                                                                                    style={{
                                                                                                        padding:
                                                                                                            '0.4rem 0.5rem',
                                                                                                    }}
                                                                                                >
                                                                                                    <input
                                                                                                        type="datetime-local"
                                                                                                        value={
                                                                                                            edit.in
                                                                                                        }
                                                                                                        onChange={(
                                                                                                            e
                                                                                                        ) =>
                                                                                                            setPunchEdits(
                                                                                                                (
                                                                                                                    prev
                                                                                                                ) => ({
                                                                                                                    ...prev,
                                                                                                                    [p._id]:
                                                                                                                        {
                                                                                                                            ...edit,
                                                                                                                            in: e
                                                                                                                                .target
                                                                                                                                .value,
                                                                                                                        },
                                                                                                                })
                                                                                                            )
                                                                                                        }
                                                                                                        style={{
                                                                                                            fontSize:
                                                                                                                '1rem',
                                                                                                            padding:
                                                                                                                '0.25rem',
                                                                                                        }}
                                                                                                    />
                                                                                                </td>
                                                                                                <td
                                                                                                    style={{
                                                                                                        padding:
                                                                                                            '0.4rem 0.5rem',
                                                                                                    }}
                                                                                                >
                                                                                                    <input
                                                                                                        type="datetime-local"
                                                                                                        value={
                                                                                                            edit.out
                                                                                                        }
                                                                                                        onChange={(
                                                                                                            e
                                                                                                        ) =>
                                                                                                            setPunchEdits(
                                                                                                                (
                                                                                                                    prev
                                                                                                                ) => ({
                                                                                                                    ...prev,
                                                                                                                    [p._id]:
                                                                                                                        {
                                                                                                                            ...edit,
                                                                                                                            out: e
                                                                                                                                .target
                                                                                                                                .value,
                                                                                                                        },
                                                                                                                })
                                                                                                            )
                                                                                                        }
                                                                                                        style={{
                                                                                                            fontSize:
                                                                                                                '1rem',
                                                                                                            padding:
                                                                                                                '0.25rem',
                                                                                                        }}
                                                                                                    />
                                                                                                    {!p.clockOutUtc && (
                                                                                                        <span
                                                                                                            style={{
                                                                                                                marginLeft:
                                                                                                                    '0.5rem',
                                                                                                                color: '#ef4444',
                                                                                                                fontWeight: 600,
                                                                                                            }}
                                                                                                        >
                                                                                                            OPEN
                                                                                                        </span>
                                                                                                    )}
                                                                                                    {p.autoClosed && (
                                                                                                        <span
                                                                                                            title="Auto-closed at 5am Chicago because employee did not clock out"
                                                                                                            style={{
                                                                                                                marginLeft:
                                                                                                                    '0.5rem',
                                                                                                                color: '#f59e0b',
                                                                                                                fontWeight: 700,
                                                                                                                fontSize:
                                                                                                                    '0.85rem',
                                                                                                                border: '1px solid #f59e0b',
                                                                                                                borderRadius:
                                                                                                                    '0.25rem',
                                                                                                                padding:
                                                                                                                    '0 0.35rem',
                                                                                                            }}
                                                                                                        >
                                                                                                            AUTO
                                                                                                        </span>
                                                                                                    )}
                                                                                                </td>
                                                                                                <td
                                                                                                    style={{
                                                                                                        padding:
                                                                                                            '0.4rem 0.5rem',
                                                                                                        textAlign:
                                                                                                            'right',
                                                                                                        fontWeight: 600,
                                                                                                        color: '#22c55e',
                                                                                                    }}
                                                                                                >
                                                                                                    {punchHours(
                                                                                                        p
                                                                                                    ).toFixed(
                                                                                                        2
                                                                                                    )}
                                                                                                </td>
                                                                                                <td
                                                                                                    style={{
                                                                                                        padding:
                                                                                                            '0.4rem 0.5rem',
                                                                                                        whiteSpace:
                                                                                                            'nowrap',
                                                                                                    }}
                                                                                                >
                                                                                                    <button
                                                                                                        className="user-button-icon"
                                                                                                        disabled={
                                                                                                            punchBusy
                                                                                                        }
                                                                                                        onClick={() =>
                                                                                                            savePunch(
                                                                                                                p
                                                                                                            )
                                                                                                        }
                                                                                                    >
                                                                                                        Save
                                                                                                    </button>
                                                                                                    <button
                                                                                                        className="user-button-icon"
                                                                                                        disabled={
                                                                                                            punchBusy
                                                                                                        }
                                                                                                        onClick={() =>
                                                                                                            removePunch(
                                                                                                                p
                                                                                                            )
                                                                                                        }
                                                                                                        style={{
                                                                                                            marginLeft:
                                                                                                                '0.4rem',
                                                                                                        }}
                                                                                                    >
                                                                                                        Delete
                                                                                                    </button>
                                                                                                </td>
                                                                                            </tr>
                                                                                        );
                                                                                    }
                                                                                )
                                                                            )}
                                                                            <tr
                                                                                style={{
                                                                                    borderTop:
                                                                                        '2px solid #22c55e',
                                                                                }}
                                                                            >
                                                                                <td
                                                                                    style={{
                                                                                        padding:
                                                                                            '0.4rem 0.5rem',
                                                                                    }}
                                                                                >
                                                                                    <input
                                                                                        type="datetime-local"
                                                                                        value={
                                                                                            newPunch.in
                                                                                        }
                                                                                        onChange={(
                                                                                            e
                                                                                        ) =>
                                                                                            setNewPunch(
                                                                                                {
                                                                                                    ...newPunch,
                                                                                                    in: e
                                                                                                        .target
                                                                                                        .value,
                                                                                                }
                                                                                            )
                                                                                        }
                                                                                        style={{
                                                                                            fontSize:
                                                                                                '1rem',
                                                                                            padding:
                                                                                                '0.25rem',
                                                                                        }}
                                                                                    />
                                                                                </td>
                                                                                <td
                                                                                    style={{
                                                                                        padding:
                                                                                            '0.4rem 0.5rem',
                                                                                    }}
                                                                                >
                                                                                    <input
                                                                                        type="datetime-local"
                                                                                        value={
                                                                                            newPunch.out
                                                                                        }
                                                                                        onChange={(
                                                                                            e
                                                                                        ) =>
                                                                                            setNewPunch(
                                                                                                {
                                                                                                    ...newPunch,
                                                                                                    out: e
                                                                                                        .target
                                                                                                        .value,
                                                                                                }
                                                                                            )
                                                                                        }
                                                                                        style={{
                                                                                            fontSize:
                                                                                                '1rem',
                                                                                            padding:
                                                                                                '0.25rem',
                                                                                        }}
                                                                                    />
                                                                                </td>
                                                                                <td />
                                                                                <td
                                                                                    style={{
                                                                                        padding:
                                                                                            '0.4rem 0.5rem',
                                                                                    }}
                                                                                >
                                                                                    <button
                                                                                        className="user-button-icon"
                                                                                        disabled={
                                                                                            punchBusy
                                                                                        }
                                                                                        onClick={() =>
                                                                                            addPunch(
                                                                                                r.employeeId
                                                                                            )
                                                                                        }
                                                                                    >
                                                                                        + Add Punch
                                                                                    </button>
                                                                                </td>
                                                                            </tr>
                                                                        </tbody>
                                                                    </table>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </Fragment>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </>
                        )}
                    </>
                )}

                {tab === 'employees' && employees && (
                    <>
                        <h3 className="transaction-container__text">
                            <span className="red-span">
                                {editingId ? 'Edit Employee' : 'Add Employee'}
                            </span>
                        </h3>

                        <span className="form-span">Name</span>
                        <input
                            className="form-input"
                            type="text"
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                        />

                        <span className="form-span">Email</span>
                        <input
                            className="form-input"
                            type="email"
                            value={form.email}
                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                        />

                        <span className="form-span">Role (optional)</span>
                        <input
                            className="form-input"
                            type="text"
                            value={form.role}
                            onChange={(e) => setForm({ ...form, role: e.target.value })}
                        />

                        <span className="form-span">
                            <input
                                type="checkbox"
                                checked={form.active}
                                onChange={(e) =>
                                    setForm({ ...form, active: e.target.checked })
                                }
                            />{' '}
                            Active
                        </span>

                        <div>
                            <Button color={'green'} margin={true} onClick={saveEmployee}>
                                {busy ? 'Saving…' : editingId ? 'Save' : 'Add'}
                            </Button>
                            {editingId && (
                                <Button color={'red'} margin={true} onClick={cancelEdit}>
                                    Cancel
                                </Button>
                            )}
                        </div>

                        <h3 className="transaction-container__text">
                            <span className="red-span">All Employees</span>
                        </h3>

                        {employees.length === 0 ? (
                            <h3 className="transaction-container__text">
                                No employees yet.
                            </h3>
                        ) : (
                            <table className="transaction-container__form--table">
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Email</th>
                                        <th>Role</th>
                                        <th>Active</th>
                                        <th>Edit</th>
                                        <th>Delete</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {employees.map((e) => (
                                        <tr id={e._id} key={e._id}>
                                            <td className="transaction-container__form--table-stage">
                                                {e.name.toUpperCase()}
                                            </td>
                                            <td className="transaction-container__form--table-value">
                                                {e.email}
                                            </td>
                                            <td className="transaction-container__form--table-value">
                                                {e.role || '—'}
                                            </td>
                                            <td className="transaction-container__form--table-value">
                                                {e.active ? 'YES' : 'NO'}
                                            </td>
                                            <td className="transaction-container__form--table-value">
                                                <button
                                                    className="user-button-icon"
                                                    onClick={() => startEdit(e)}
                                                >
                                                    Edit
                                                </button>
                                            </td>
                                            <td className="transaction-container__form--table-value">
                                                <button
                                                    className="user-button-icon"
                                                    onClick={() => removeEmployee(e._id)}
                                                >
                                                    Delete
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </>
                )}

                {tab === 'config' && config && (
                    <>
                        <h3 className="transaction-container__text">
                            <span className="red-span">Payroll Period Settings</span>
                        </h3>

                        <span className="form-span">Cadence (weeks)</span>
                        <select
                            className="select-input"
                            value={configForm.cadenceWeeks}
                            onChange={(e) =>
                                setConfigForm({
                                    ...configForm,
                                    cadenceWeeks: Number(e.target.value),
                                })
                            }
                        >
                            <option value={1}>Weekly (1)</option>
                            <option value={2}>Bi-weekly (2)</option>
                            <option value={4}>Monthly-ish (4)</option>
                        </select>

                        <span className="form-span">Anchor date</span>
                        <input
                            className="form-input"
                            type="date"
                            value={configForm.anchorDate}
                            onChange={(e) =>
                                setConfigForm({
                                    ...configForm,
                                    anchorDate: e.target.value,
                                })
                            }
                        />

                        <Button color={'green'} margin={true} onClick={saveConfig}>
                            {busy ? 'Saving…' : 'Save'}
                        </Button>
                    </>
                )}

                <Button
                    color={'red'}
                    margin={true}
                    onClick={() => history.push('/dashboard')}
                >
                    Go back
                </Button>
            {tab === 'stats' && (
                    <>
                        <div
                            style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                justifyContent: 'center',
                                gap: '0.5rem',
                                margin: '0.5rem 0 1.25rem',
                            }}
                        >
                            <Button
                                color={statsPreset === 'today' ? 'green' : 'blue'}
                                margin={true}
                                onClick={() => applyPreset('today')}
                            >
                                Today
                            </Button>
                            <Button
                                color={statsPreset === 'week' ? 'green' : 'blue'}
                                margin={true}
                                onClick={() => applyPreset('week')}
                            >
                                Last 7 Days
                            </Button>
                            <Button
                                color={statsPreset === 'month' ? 'green' : 'blue'}
                                margin={true}
                                onClick={() => applyPreset('month')}
                            >
                                Last 30 Days
                            </Button>
                            <Button
                                color={statsPreset === 'ytd' ? 'green' : 'blue'}
                                margin={true}
                                onClick={() => applyPreset('ytd')}
                            >
                                Year to Date
                            </Button>
                            <Button
                                color={statsPreset === 'custom' ? 'green' : 'blue'}
                                margin={true}
                                onClick={() => setStatsPreset('custom')}
                            >
                                Custom
                            </Button>
                        </div>

                        {statsPreset === 'custom' && (
                            <div
                                style={{
                                    display: 'flex',
                                    gap: '1rem',
                                    alignItems: 'center',
                                    flexWrap: 'wrap',
                                    justifyContent: 'center',
                                    marginBottom: '1.25rem',
                                    fontSize: '1.15rem',
                                }}
                            >
                                <label>
                                    From:{' '}
                                    <input
                                        type="date"
                                        value={statsFrom}
                                        onChange={(e) =>
                                            setStatsFrom(e.target.value)
                                        }
                                        style={{
                                            fontSize: '1.1rem',
                                            padding: '0.4rem 0.6rem',
                                        }}
                                    />
                                </label>
                                <label>
                                    To:{' '}
                                    <input
                                        type="date"
                                        value={statsTo}
                                        onChange={(e) =>
                                            setStatsTo(e.target.value)
                                        }
                                        style={{
                                            fontSize: '1.1rem',
                                            padding: '0.4rem 0.6rem',
                                        }}
                                    />
                                </label>
                                <Button
                                    color="green"
                                    margin={true}
                                    onClick={() => {
                                        const t = new Date(statsTo);
                                        t.setUTCDate(t.getUTCDate() + 1);
                                        loadStats(
                                            statsFrom,
                                            t.toISOString().slice(0, 10)
                                        );
                                    }}
                                >
                                    Load
                                </Button>
                            </div>
                        )}

                        {statsLoading && (
                            <h3 className="transaction-container__text">
                                Loading stats...
                            </h3>
                        )}

                        {stats && !statsLoading && (
                            <>
                                <p
                                    style={{
                                        textAlign: 'center',
                                        fontSize: '1.35rem',
                                        fontWeight: 600,
                                        margin: '0 0 1rem',
                                    }}
                                >
                                    <strong style={{ color: '#22c55e' }}>
                                        {fmtDay(stats.from.slice(0, 10))}
                                    </strong>{' '}
                                    &nbsp;→&nbsp;{' '}
                                    <strong style={{ color: '#22c55e' }}>
                                        {fmtDay(
                                            new Date(
                                                new Date(stats.to).getTime() -
                                                    1
                                            )
                                                .toISOString()
                                                .slice(0, 10)
                                        )}
                                    </strong>
                                </p>

                                <div
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns:
                                            'repeat(auto-fit, minmax(13rem, 1fr))',
                                        gap: '1rem',
                                        margin: '0 auto 1.5rem',
                                        maxWidth: '60rem',
                                    }}
                                >
                                    {[
                                        {
                                            value: fmtHours(stats.totals.hours),
                                            label: 'TOTAL HOURS',
                                        },
                                        {
                                            value: String(stats.totals.days),
                                            label: 'DAYS WORKED',
                                        },
                                        {
                                            value: String(
                                                stats.totals.employees
                                            ),
                                            label: 'EMPLOYEES',
                                        },
                                    ].map((c) => (
                                        <div
                                            key={c.label}
                                            style={{
                                                border: '3px solid #22c55e',
                                                borderRadius: '0.75rem',
                                                padding: '1.15rem 0.75rem',
                                                textAlign: 'center',
                                            }}
                                        >
                                            <div
                                                style={{
                                                    fontSize: '3rem',
                                                    fontWeight: 800,
                                                    color: '#22c55e',
                                                    lineHeight: 1,
                                                }}
                                            >
                                                {c.value}
                                            </div>
                                            <div
                                                style={{
                                                    marginTop: '0.55rem',
                                                    fontSize: '1rem',
                                                    fontWeight: 700,
                                                    letterSpacing: '0.08rem',
                                                }}
                                            >
                                                {c.label}
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <h2
                                    style={{
                                        textAlign: 'center',
                                        marginTop: '1.5rem',
                                        marginBottom: '0.5rem',
                                    }}
                                >
                                    By Employee
                                </h2>
                                {stats.byEmployee.length === 0 ? (
                                    <p
                                        style={{
                                            textAlign: 'center',
                                            fontSize: '1.15rem',
                                        }}
                                    >
                                        No hours in this range.
                                    </p>
                                ) : (
                                    <div
                                        style={{
                                            overflowX: 'auto',
                                            margin: '0 auto 1rem',
                                            maxWidth: '64rem',
                                        }}
                                    >
                                        <table
                                            style={{
                                                width: '100%',
                                                borderCollapse: 'collapse',
                                                fontSize: '1.25rem',
                                            }}
                                        >
                                            <thead>
                                                <tr
                                                    style={{
                                                        borderBottom:
                                                            '3px solid #22c55e',
                                                    }}
                                                >
                                                    <th
                                                        style={{
                                                            textAlign: 'left',
                                                            padding:
                                                                '0.75rem 1rem',
                                                            fontSize: '1.05rem',
                                                            letterSpacing:
                                                                '0.05rem',
                                                        }}
                                                    >
                                                        EMPLOYEE
                                                    </th>
                                                    <th
                                                        style={{
                                                            textAlign: 'right',
                                                            padding:
                                                                '0.75rem 1rem',
                                                            fontSize: '1.05rem',
                                                            letterSpacing:
                                                                '0.05rem',
                                                        }}
                                                    >
                                                        DAYS
                                                    </th>
                                                    <th
                                                        style={{
                                                            textAlign: 'right',
                                                            padding:
                                                                '0.75rem 1rem',
                                                            fontSize: '1.05rem',
                                                            letterSpacing:
                                                                '0.05rem',
                                                        }}
                                                    >
                                                        HOURS
                                                    </th>

                                                </tr>
                                            </thead>
                                            <tbody>
                                                {stats.byEmployee.map(
                                                    (r, i) => (
                                                        <tr
                                                            key={r.employeeId}
                                                            style={{
                                                                borderBottom:
                                                                    '1px solid rgba(128,128,128,0.3)',
                                                                background:
                                                                    i % 2 === 1
                                                                        ? 'rgba(128,128,128,0.08)'
                                                                        : undefined,
                                                            }}
                                                        >
                                                            <td
                                                                style={{
                                                                    padding:
                                                                        '0.85rem 1rem',
                                                                    fontWeight: 600,
                                                                }}
                                                            >
                                                                {r.name}
                                                            </td>
                                                            <td
                                                                style={{
                                                                    padding:
                                                                        '0.85rem 1rem',
                                                                    textAlign:
                                                                        'right',
                                                                }}
                                                            >
                                                                {r.days}
                                                            </td>
                                                            <td
                                                                style={{
                                                                    padding:
                                                                        '0.85rem 1rem',
                                                                    textAlign:
                                                                        'right',
                                                                    fontWeight: 700,
                                                                    color: '#22c55e',
                                                                }}
                                                            >
                                                                {fmtHours(
                                                                    r.hours
                                                                )}
                                                            </td>

                                                        </tr>
                                                    )
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                <h2
                                    style={{
                                        textAlign: 'center',
                                        marginTop: '2rem',
                                        marginBottom: '0.25rem',
                                    }}
                                >
                                    By Day
                                </h2>
                                <p
                                    style={{
                                        textAlign: 'center',
                                        fontSize: '1rem',
                                        marginTop: 0,
                                        marginBottom: '0.75rem',
                                        fontStyle: 'italic',
                                    }}
                                >
                                    Tap any day to see who clocked in.
                                </p>
                                {stats.byDay.length === 0 ? (
                                    <p
                                        style={{
                                            textAlign: 'center',
                                            fontSize: '1.15rem',
                                        }}
                                    >
                                        No hours in this range.
                                    </p>
                                ) : (
                                    <div
                                        style={{
                                            overflowX: 'auto',
                                            margin: '0 auto',
                                            maxWidth: '64rem',
                                        }}
                                    >
                                        <table
                                            style={{
                                                width: '100%',
                                                borderCollapse: 'collapse',
                                                fontSize: '1.25rem',
                                            }}
                                        >
                                            <thead>
                                                <tr
                                                    style={{
                                                        borderBottom:
                                                            '3px solid #22c55e',
                                                    }}
                                                >
                                                    <th
                                                        style={{
                                                            textAlign: 'left',
                                                            padding:
                                                                '0.75rem 1rem',
                                                            fontSize: '1.05rem',
                                                            letterSpacing:
                                                                '0.05rem',
                                                        }}
                                                    >
                                                        DATE
                                                    </th>
                                                    <th
                                                        style={{
                                                            textAlign: 'right',
                                                            padding:
                                                                '0.75rem 1rem',
                                                            fontSize: '1.05rem',
                                                            letterSpacing:
                                                                '0.05rem',
                                                        }}
                                                    >
                                                        TOTAL HOURS
                                                    </th>
                                                    <th
                                                        style={{
                                                            textAlign: 'right',
                                                            padding:
                                                                '0.75rem 1rem',
                                                            fontSize: '1.05rem',
                                                            letterSpacing:
                                                                '0.05rem',
                                                        }}
                                                    >
                                                        EMPLOYEES
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {stats.byDay.map((d, i) => {
                                                    const expanded =
                                                        statsExpandedDate ===
                                                        d.date;
                                                    return (
                                                        <>
                                                            <tr
                                                                key={d.date}
                                                                onClick={() =>
                                                                    setStatsExpandedDate(
                                                                        expanded
                                                                            ? null
                                                                            : d.date
                                                                    )
                                                                }
                                                                style={{
                                                                    cursor: 'pointer',
                                                                    borderBottom:
                                                                        '1px solid rgba(128,128,128,0.3)',
                                                                    background: expanded
                                                                        ? 'rgba(34,197,94,0.12)'
                                                                        : i %
                                                                              2 ===
                                                                          1
                                                                        ? 'rgba(128,128,128,0.08)'
                                                                        : undefined,
                                                                }}
                                                            >
                                                                <td
                                                                    style={{
                                                                        padding:
                                                                            '0.95rem 1rem',
                                                                        fontWeight: 700,
                                                                    }}
                                                                >
                                                                    <span
                                                                        style={{
                                                                            color: '#22c55e',
                                                                            marginRight:
                                                                                '0.5rem',
                                                                        }}
                                                                    >
                                                                        {expanded
                                                                            ? '▼'
                                                                            : '▶'}
                                                                    </span>
                                                                    {fmtDay(
                                                                        d.date
                                                                    )}
                                                                </td>
                                                                <td
                                                                    style={{
                                                                        padding:
                                                                            '0.95rem 1rem',
                                                                        textAlign:
                                                                            'right',
                                                                        fontWeight: 700,
                                                                        color: '#22c55e',
                                                                    }}
                                                                >
                                                                    {fmtHours(
                                                                        d.hours
                                                                    )}
                                                                </td>
                                                                <td
                                                                    style={{
                                                                        padding:
                                                                            '0.95rem 1rem',
                                                                        textAlign:
                                                                            'right',
                                                                    }}
                                                                >
                                                                    {
                                                                        d
                                                                            .employees
                                                                            .length
                                                                    }
                                                                </td>
                                                            </tr>
                                                            {expanded && (
                                                                <tr
                                                                    key={
                                                                        d.date +
                                                                        '-detail'
                                                                    }
                                                                >
                                                                    <td
                                                                        colSpan={
                                                                            3
                                                                        }
                                                                        style={{
                                                                            padding:
                                                                                '0.5rem 2rem 1rem',
                                                                            background:
                                                                                'rgba(34,197,94,0.06)',
                                                                            borderBottom:
                                                                                '2px solid rgba(34,197,94,0.3)',
                                                                        }}
                                                                    >
                                                                        <table
                                                                            style={{
                                                                                width: '100%',
                                                                                fontSize:
                                                                                    '1.15rem',
                                                                            }}
                                                                        >
                                                                            <tbody>
                                                                                {d.employees.map(
                                                                                    (
                                                                                        e
                                                                                    ) => (
                                                                                        <tr
                                                                                            key={
                                                                                                e.employeeId
                                                                                            }
                                                                                        >
                                                                                            <td
                                                                                                style={{
                                                                                                    padding:
                                                                                                        '0.45rem 0',
                                                                                                }}
                                                                                            >
                                                                                                {
                                                                                                    e.name
                                                                                                }
                                                                                            </td>
                                                                                            <td
                                                                                                style={{
                                                                                                    padding:
                                                                                                        '0.45rem 0',
                                                                                                    textAlign:
                                                                                                        'right',
                                                                                                    fontWeight: 700,
                                                                                                    color: '#22c55e',
                                                                                                }}
                                                                                            >
                                                                                                {fmtHours(
                                                                                                    e.hours
                                                                                                )}
                                                                                            </td>
                                                                                        </tr>
                                                                                    )
                                                                                )}
                                                                            </tbody>
                                                                        </table>
                                                                    </td>
                                                                </tr>
                                                            )}
                                                        </>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </>
                        )}
                    </>
                )}

                {tab === 'quickbooks' && (
                    <>
                        {qboBanner && (
                            <h3 className="transaction-container__text">
                                <span className="green-span">{qboBanner}</span>
                            </h3>
                        )}

                        {!qboStatus && (
                            <h3 className="transaction-container__text">
                                Loading QuickBooks status…
                            </h3>
                        )}

                        {qboStatus && !qboStatus.configured && (
                            <div
                                style={{
                                    border: '3px solid #ef4444',
                                    borderRadius: '0.5rem',
                                    padding: '1rem',
                                    margin: '0.5rem auto',
                                    maxWidth: '42rem',
                                }}
                            >
                                <h3>QuickBooks not configured</h3>
                                <p>
                                    Set <code>QBO_CLIENT_ID</code> and{' '}
                                    <code>QBO_CLIENT_SECRET</code> in the server
                                    .env. Register an app at{' '}
                                    <strong>developer.intuit.com</strong> and
                                    add this redirect URI in the app settings:
                                </p>
                                <pre
                                    style={{
                                        background: 'rgba(100,100,100,0.2)',
                                        padding: '0.5rem',
                                        borderRadius: '0.25rem',
                                        overflow: 'auto',
                                    }}
                                >
                                    {qboStatus.redirectUri}
                                </pre>
                            </div>
                        )}

                        {qboStatus &&
                            qboStatus.configured &&
                            !qboStatus.connected && (
                                <div
                                    style={{
                                        border: '3px solid #3b82f6',
                                        borderRadius: '0.5rem',
                                        padding: '1rem',
                                        margin: '0.5rem auto',
                                        maxWidth: '42rem',
                                    }}
                                >
                                    <h3>Not connected to QuickBooks</h3>
                                    <p>
                                        Click below to authorize porkysbbq to
                                        push time activities to your QBO
                                        company.
                                    </p>
                                    <Button
                                        color="green"
                                        margin={true}
                                        onClick={qboConnect}
                                    >
                                        Connect to QuickBooks
                                    </Button>
                                </div>
                            )}

                        {qboStatus && qboStatus.connected && (
                            <div
                                style={{
                                    border: '3px solid #22c55e',
                                    borderRadius: '0.5rem',
                                    padding: '1rem',
                                    margin: '0.5rem auto',
                                    maxWidth: '42rem',
                                }}
                            >
                                <h3 style={{ marginTop: 0 }}>
                                    Connected
                                </h3>
                                <p style={{ margin: '0.25rem 0' }}>
                                    Realm / Company ID:{' '}
                                    <strong>{qboStatus.realmId}</strong>
                                </p>
                                <p style={{ margin: '0.25rem 0' }}>
                                    Environment:{' '}
                                    <strong>{qboStatus.environment}</strong>
                                </p>
                                {qboStatus.lastRun && (
                                    <p style={{ margin: '0.5rem 0' }}>
                                        Last run:{' '}
                                        <strong>
                                            {fmtDate(
                                                qboStatus.lastRun.runAtUtc
                                            )}
                                        </strong>{' '}
                                        —{' '}
                                        {
                                            qboStatus.lastRun.activitiesCreated
                                        }{' '}
                                        activities created for period{' '}
                                        {fmtDate(
                                            qboStatus.lastRun.periodStart
                                        )}{' '}
                                        —{' '}
                                        {fmtDate(
                                            qboStatus.lastRun.periodEnd
                                        )}
                                    </p>
                                )}
                                <div style={{ marginTop: '1rem' }}>
                                    <Button
                                        color="green"
                                        margin={true}
                                        onClick={runPayroll}
                                    >
                                        {qboBusy
                                            ? 'Running…'
                                            : 'Run Payroll → QuickBooks'}
                                    </Button>
                                    <Button
                                        color="red"
                                        margin={true}
                                        onClick={qboDisconnect}
                                    >
                                        Disconnect
                                    </Button>
                                </div>
                            </div>
                        )}

                        {qboRun && (
                            <div
                                style={{
                                    border: '2px solid #888',
                                    borderRadius: '0.5rem',
                                    padding: '1rem',
                                    margin: '1rem auto',
                                    maxWidth: '42rem',
                                }}
                            >
                                <h3 style={{ marginTop: 0 }}>
                                    Run Result
                                </h3>
                                <p>
                                    Period:{' '}
                                    <strong>
                                        {fmtDate(qboRun.periodStart)} —{' '}
                                        {fmtDate(qboRun.periodEnd)}
                                    </strong>
                                </p>
                                <p>
                                    Activities created:{' '}
                                    <strong style={{ color: '#22c55e' }}>
                                        {qboRun.activitiesCreated}
                                    </strong>
                                </p>
                                {qboRun.unmatchedEmployees.length > 0 && (
                                    <div>
                                        <strong
                                            style={{ color: '#ef4444' }}
                                        >
                                            Unmatched employees (not in QBO):
                                        </strong>
                                        <ul>
                                            {qboRun.unmatchedEmployees.map(
                                                (n) => (
                                                    <li key={n}>{n}</li>
                                                )
                                            )}
                                        </ul>
                                    </div>
                                )}
                                {qboRun.errors.length > 0 && (
                                    <div>
                                        <strong
                                            style={{ color: '#ef4444' }}
                                        >
                                            Errors:
                                        </strong>
                                        <ul>
                                            {qboRun.errors.map((e, i) => (
                                                <li key={i}>{e}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                {qboRun.details.length > 0 && (
                                    <details style={{ marginTop: '0.75rem' }}>
                                        <summary>
                                            Per-activity details (
                                            {qboRun.details.length})
                                        </summary>
                                        <table
                                            className="transaction-table"
                                            style={{
                                                marginTop: '0.5rem',
                                            }}
                                        >
                                            <thead>
                                                <tr>
                                                    <th>Employee</th>
                                                    <th>Date</th>
                                                    <th>Hours</th>
                                                    <th>Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {qboRun.details.map(
                                                    (d, i) => (
                                                        <tr key={i}>
                                                            <td>
                                                                {d.employee}
                                                            </td>
                                                            <td>{d.date}</td>
                                                            <td>
                                                                {d.hours.toFixed(
                                                                    2
                                                                )}
                                                            </td>
                                                            <td
                                                                style={{
                                                                    color:
                                                                        d.status ===
                                                                        'error'
                                                                            ? '#ef4444'
                                                                            : d.status ===
                                                                              'created'
                                                                            ? '#22c55e'
                                                                            : '#888',
                                                                }}
                                                            >
                                                                {d.status}
                                                                {d.message
                                                                    ? ': ' + d.message
                                                                    : ''}
                                                            </td>
                                                        </tr>
                                                    )
                                                )}
                                            </tbody>
                                        </table>
                                    </details>
                                )}
                            </div>
                        )}
                    </>
                )}

                </FlexContainer>
        </Container>
    );
};

export default PayrollAdminPage;
